/**
 * FileDetailsModal Component
 * Modal displaying detailed file information
 */

import React from 'react';
import { Modal } from '@/components/ui/overlays';
import { Stack, Group } from '@/components/ui/layout';
import { Divider } from '@/components/ui/data-display';
import { Text, Badge, Button } from '@/components/ui/core';
import { CopyButton } from '@mantine/core';
import { IconDownload, IconCopy } from '@tabler/icons-react';
import { FileRecordModelView } from '../types';

interface FileDetailsModalProps {
  file: FileRecordModelView | null;
  opened: boolean;
  onClose: () => void;
  onDownload: (fileId: string, fileName: string) => void;
}

export const FileDetailsModal: React.FC<FileDetailsModalProps> = ({
  file,
  opened,
  onClose,
  onDownload,
}) => {
  if (!file) return null;

  const formatFileSize = (bytes: number): string => {
    const sizes = ['B', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 B';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
  };

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="File Details"
      size="lg"
    >
      <Stack gap="md">
        {/* File Info */}
        <Group justify="space-between">
          <Text fw={600}>File Name:</Text>
          <Text>{file.fileName}</Text>
        </Group>

        <Divider />

        <Group justify="space-between">
          <Text fw={600}>File Size:</Text>
          <Text>{formatFileSize(file.fileSize)}</Text>
        </Group>

        <Group justify="space-between">
          <Text fw={600}>MIME Type:</Text>
          <Text>{file.mimeType}</Text>
        </Group>

        <Group justify="space-between">
          <Text fw={600}>Status:</Text>
          <Badge
            color={
              file.status === 'uploaded' ? 'green' :
              file.status === 'pending' ? 'yellow' : 'gray'
            }
          >
            {file.status}
          </Badge>
        </Group>

        <Divider />

        <Group justify="space-between">
          <Text fw={600}>Domain:</Text>
          <Text>{file.domain || 'N/A'}</Text>
        </Group>

        <Group justify="space-between">
          <Text fw={600}>Folder Path:</Text>
          <Text>{file.folderPath || '/'}</Text>
        </Group>

        <Divider />

        <Group justify="space-between">
          <Text fw={600}>Uploaded By:</Text>
          <Text>
            {file.userFirstName || file.userLastName
              ? `${file.userFirstName} ${file.userLastName}`.trim()
              : file.userEmail || 'Unknown'}
          </Text>
        </Group>

        <Group justify="space-between">
          <Text fw={600}>Uploaded At:</Text>
          <Text>{formatDate(file.createdAt)}</Text>
        </Group>

        <Group justify="space-between">
          <Text fw={600}>Last Updated:</Text>
          <Text>{formatDate(file.updatedAt)}</Text>
        </Group>

        <Divider />

        {/* Storage Info */}
        <Group justify="space-between">
          <Text fw={600}>Storage Key:</Text>
          <CopyButton value={file.storageKey}>
            {({ copied, copy }) => (
              <Button
                size="xs"
                variant="subtle"
                leftSection={<IconCopy size={14} />}
                onClick={copy}
                color={copied ? 'teal' : 'blue'}
              >
                {copied ? 'Copied' : 'Copy'}
              </Button>
            )}
          </CopyButton>
        </Group>

        <Group justify="space-between">
          <Text fw={600}>URL:</Text>
          <CopyButton value={file.url}>
            {({ copied, copy }) => (
              <Button
                size="xs"
                variant="subtle"
                leftSection={<IconCopy size={14} />}
                onClick={copy}
                color={copied ? 'teal' : 'blue'}
              >
                {copied ? 'Copied' : 'Copy'}
              </Button>
            )}
          </CopyButton>
        </Group>

        <Group justify="flex-end" mt="md">
          <Button
            leftSection={<IconDownload size={16} />}
            onClick={() => onDownload(file.id, file.fileName)}
          >
            Download
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
